/**
 * hiprint 服务封装
 * 提供 hiprint 模板管理、渲染、打印等功能
 */

// 动态导入 hiprint 模块
let hiprintModule = null;

export default {
  /**
   * 初始化 hiprint 模块引用
   */
  init(hiprint) {
    hiprintModule = hiprint;
  },

  /**
   * 检查 hiprint 是否可用
   */
  isAvailable() {
    // 检查多种可能的 hiprint 引用方式
    if (hiprintModule && hiprintModule.PrintTemplate) {
      return true;
    }
    if (window.hiprint && window.hiprint.PrintTemplate) {
      return true;
    }
    // 尝试从 Vue 插件获取
    if (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint) {
      const hiprint = window.Vue.prototype.$hiprint;
      if (hiprint.PrintTemplate) {
        hiprintModule = hiprint;
        return true;
      }
    }
    return false;
  },

  /**
   * 获取 PrintTemplate 构造函数
   */
  getPrintTemplate() {
    if (hiprintModule && hiprintModule.PrintTemplate) {
      return hiprintModule.PrintTemplate;
    }
    if (window.hiprint && window.hiprint.PrintTemplate) {
      return window.hiprint.PrintTemplate;
    }
    if (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint) {
      return window.Vue.prototype.$hiprint.PrintTemplate;
    }
    return null;
  },

  /**
   * 创建打印模板实例
   * @param {Object} templateJson - hiprint 模板 JSON
   * @returns {Object} PrintTemplate 实例
   */
  createTemplate(templateJson) {
    const PrintTemplate = this.getPrintTemplate();
    if (!PrintTemplate) {
      throw new Error('hiprint 未初始化');
    }
    return new PrintTemplate({
      template: templateJson
    });
  },

  /**
   * 从模板 JSON 和数据渲染预览 HTML
   * @param {Object} templateJson - hiprint 模板 JSON
   * @param {Object} data - 业务数据
   * @returns {string} 渲染后的 HTML 字符串
   */
  renderHtml(templateJson, data) {
    const template = this.createTemplate(templateJson);
    const htmlElements = template.getHtml(data);
    if (htmlElements && htmlElements[0]) {
      return htmlElements[0].innerHTML;
    }
    return '';
  },

  /**
   * 异步渲染预览 HTML
   * @param {Object} templateJson - hiprint 模板 JSON
   * @param {Object} data - 业务数据
   * @returns {Promise<string>} 渲染后的 HTML 字符串
   */
  async renderHtmlAsync(templateJson, data) {
    const template = this.createTemplate(templateJson);
    const html = await template.getHtmlAsync(data);
    return html;
  },

  /**
   * 打印模板
   * @param {Object} templateJson - hiprint 模板 JSON
   * @param {Object} data - 业务数据
   */
  print(templateJson, data) {
    const template = this.createTemplate(templateJson);
    template.print(data);
  },

  /**
   * 在指定元素中渲染模板
   * @param {Object} templateJson - hiprint 模板 JSON
   * @param {Object} data - 业务数据
   * @param {HTMLElement} element - 目标 DOM 元素
   */
  renderToElement(templateJson, data, element) {
    const template = this.createTemplate(templateJson);
    const htmlElements = template.getHtml(data);
    if (htmlElements && htmlElements[0] && element) {
      element.innerHTML = htmlElements[0].innerHTML;
    }
  },

  /**
   * 获取模板的 JSON 定义
   * @param {Object} templateInstance - PrintTemplate 实例
   * @returns {Object} 模板 JSON
   */
  getTemplateJson(templateInstance) {
    return templateInstance.getJson();
  },

  /**
   * 启动模板设计器
   * @param {HTMLElement} container - 设计器容器元素
   * @param {Object} templateJson - 初始模板 JSON（可选）
   * @param {Object} options - 设计器配置
   * @returns {Object} PrintTemplate 实例
   */
  openDesigner(container, templateJson = null, options = {}) {
    const template = this.createTemplate(templateJson || this.getDefaultTemplate());
    template.design(container, options);
    return template;
  },

  /**
   * 获取默认模板
   * @returns {Object} 默认模板 JSON
   */
  getDefaultTemplate() {
    return {
      panels: [{
        index: 0,
        name: 1,
        height: 297,
        width: 210,
        printElements: [
          {
            tid: 'provider',
            options: {
              left: 60,
              top: 15,
              height: 15,
              width: 90,
              testData: '@标题',
              textAlign: 'center',
              title: '标题',
              qid: 'title',
              field: 'title',
              hideTitle: true,
              fontSize: 15
            }
          }
        ],
        paperNumberLeft: 0,
        paperNumberTop: 0
      }]
    };
  },

  /**
   * 获取支持的纸张类型
   * @returns {Array} 纸张类型列表
   */
  getPaperTypes() {
    return [
      { name: 'A4', width: 210, height: 297 },
      { name: 'A5', width: 148, height: 210 },
      { name: 'A6', width: 105, height: 148 },
      { name: 'B5', width: 176, height: 250 },
      { name: 'B6', width: 125, height: 176 },
      { name: '自定义', width: 210, height: 297 }
    ];
  }
};
